import React, { useState, useEffect, useContext } from "react";
import {
  Label,
  Form,
  FormGroup,
  Row,
  Col,
  Input,
  InputGroup,
} from "reactstrap";
import { useForm, useController, Controller } from "react-hook-form";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { showAlert } from "../../../Services/Shared.service";
import SharedDataContext from "../../../_helper/SharedData";
import CustomizerContext from "../../../_helper/Customizer";
import {
  getDriverSchemeDetails,
  updateDriverSchemeDetails,
  createDriverSchemeDetails
} from "../../../Services/Driver.service";

const UnifiedDriverSchemeForm = ({ setIsModal, triggerRefresh, editId }) => {
  const { cityData } = useContext(SharedDataContext);
  const { mix_background_layout } = useContext(CustomizerContext);
  const [loading, setLoading] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      name: "",
      isSubscription: false,
      isHide: false,
      minBalance: 0,
      applicableFromDate: new Date(),
      applicableToDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
    },
  });

  useEffect(() => {
    const loadSchemeData = async () => {
      if (editId && cityData?.length > 0) {
        setLoading(true);
        try {
          const resp = await getDriverSchemeDetails(editId);
          if (resp.status === 200) {
            const data = resp.data;
            reset({
              name: data.driverTypeName,
              ServiceCity: cityData.find((c) => c.id === data.serviceCityId),
              isSubscription: !!data.isSubscription,
              isHide: !!data.isHide,
              minBalance: data.minimumBalance || 0,
              applicableFromDate: data.applicableFrom ? new Date(data.applicableFrom) : new Date(),
              applicableToDate: data.applicableTo ? new Date(data.applicableTo) : new Date(),
            });
          }
        } catch (error) {
          console.error("Error prefilling form:", error);
          showAlert("error", "Failed to load scheme details");
        } finally {
          setLoading(false);
        }
      }
    };

    loadSchemeData();
  }, [editId, cityData, reset]);

  const { ServiceCity, isSubscription, isHide } = watch();

  // Date Controller for Applicable From
  const applicableFromDateField = useController({
    name: "applicableFromDate",
    control,
    rules: { required: "Required" },
  }).field;

  // Date Controller for Applicable To
  const applicableToDateField = useController({
    name: "applicableToDate",
    control,
    rules: { required: "Required" },
  }).field;

  const onSubmit = async (formValues) => {
    setLoading(true);
    try {
      if (editId) {
        // Consolidated PUT Update
        const updateObj = {
          driverTypeId: editId,
          driverTypeName: formValues.name.trim(),
          serviceCityId: formValues.ServiceCity?.id,
          minimumBalance: parseFloat(formValues.minBalance),
          isSubscription: formValues.isSubscription,
          isHide: formValues.isHide,
          applicableFrom: formValues.applicableFromDate.toISOString(),
          applicableTo: formValues.applicableToDate.toISOString(),
        };

        await updateDriverSchemeDetails(updateObj);
        showAlert("success", "Scheme updated successfully!");
      } else {
        // Consolidated POST Create
        const createObj = {
          driverTypeName: formValues.name.trim(),
          serviceCityId: formValues.ServiceCity?.id,
          minimumBalance: parseFloat(formValues.minBalance),
          isSubscription: formValues.isSubscription,
          isHide: formValues.isHide,
          applicableFrom: formValues.applicableFromDate.toISOString(),
          applicableTo: formValues.applicableToDate.toISOString(),
        };

        await createDriverSchemeDetails(createObj);
        showAlert("success", "Driver Scheme created successfully!");

      }
      triggerRefresh();
      setIsModal(false);
    } catch (error) {
      console.error("Error saving scheme:", error);
      showAlert("error", error.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className="p-3">
      <Row>
        {/* Section 1: General Details */}
        <Col md="12">
          <h5 className="mb-3" style={{ color: "#39B54A", borderBottom: "1px solid #eee", paddingBottom: "10px" }}>
            General Details
          </h5>
        </Col>

        <FormGroup className="col-lg-6">
          <Label className="fw-bold small">Scheme Name</Label>
          <input
            className="form-control"
            type="text"
            placeholder="Driver Scheme Name"
            {...register("name", { required: "Scheme Name is required" })}
          />
          {errors.name && <span className="text-danger small">{errors.name.message}</span>}
        </FormGroup>

        <FormGroup className="col-lg-6">
          <Label className="fw-bold small">Service City</Label>
          <Select
            options={cityData}
            value={ServiceCity}
            onChange={(val) => setValue("ServiceCity", val)}
            placeholder="Select City"
          />
          {errors.ServiceCity && <span className="text-danger small">Required</span>}
        </FormGroup>

        <FormGroup className="col-lg-6">
          <Label className="fw-bold small">Minimum Wallet Balance</Label>
          <input
            className="form-control"
            type="number"
            placeholder="0.00"
            {...register("minBalance", { required: "Required" })}
          />
        </FormGroup>

        <Col lg="6" className="d-flex align-items-center gap-4 mt-2">
          <div className="d-flex align-items-center gap-2">
            <Controller
              name="isSubscription"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="checkbox"
                  style={{ width: "18px", height: "18px" }}
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              )}
            />
            <Label className="mb-0 small fw-bold">Is Subscription</Label>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Controller
              name="isHide"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="checkbox"
                  style={{ width: "18px", height: "18px" }}
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              )}
            />
            <Label className="mb-0 small fw-bold">Is Hide</Label>
          </div>
        </Col>

        {/* Section 2: Wallet Configuration */}
        <Col md="12" className="mt-3">
          <h6 className="mb-3" style={{ color: "#39B54A", borderBottom: "1px solid #eee", paddingBottom: "5px" }}>
            Validity Period
          </h6>
        </Col>

        <FormGroup className="col-lg-6">
          <Label className="fw-bold small">Applicable From</Label>
          <InputGroup>
            <DatePicker
              className="form-control"
              selected={applicableFromDateField.value}
              onChange={(date) => applicableFromDateField.onChange(date)}
              showTimeSelect
              dateFormat="MMMM d, yyyy h:mm aa"
            />
          </InputGroup>
        </FormGroup>

        <FormGroup className="col-lg-6">
          <Label className="fw-bold small">Applicable To</Label>
          <InputGroup>
            <DatePicker
              className="form-control"
              selected={applicableToDateField.value}
              onChange={(date) => applicableToDateField.onChange(date)}
              showTimeSelect
              dateFormat="MMMM d, yyyy h:mm aa"
            />
          </InputGroup>
        </FormGroup>
      </Row>

      <div className="d-flex justify-content-center gap-3 mt-5">
        <button
          type="submit"
          className="btn"
          disabled={loading}
          style={{
            backgroundColor: "#39B54A",
            color: "white",
            height:"35px",
            width: "200px",
            borderRadius: "10px",
            fontWeight: "600"
          }}
        >
          {loading ? (editId ? "Updating..." : "Creating...") : (editId ? "Update Scheme" : "Create Scheme")}
        </button>
      </div>
    </Form>
  );
};

export default UnifiedDriverSchemeForm;
